/**
 * Self-contained "Schedule callback" trigger: a button that owns
 * its own modal state so the parent (InteractionRecorder) doesn't
 * absorb the extra cognitive complexity of modal lifecycle.
 *
 * Renders nothing when `interactionId` is null — useful for the
 * "no interaction yet" state where there's nothing to schedule from.
 */
import { useState } from 'react';

import ScheduleCallbackModal from './ScheduleCallbackModal';
import type { ScheduledCallback } from '../../services/interactions.service';
import { tokens } from '../forms/tokens';

interface Props {
  interactionId: string | null;
  initialPurpose?: string;
  disabled?: boolean;
  onScheduled?: (appointment: ScheduledCallback) => void;
}

export default function ScheduleCallbackButton({
  interactionId,
  initialPurpose,
  disabled,
  onScheduled,
}: Readonly<Props>): JSX.Element | null {
  const [open, setOpen] = useState(false);

  if (!interactionId) return null;

  const handleScheduled = (appointment: ScheduledCallback): void => {
    setOpen(false);
    onScheduled?.(appointment);
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        disabled={disabled}
        style={btnStyle}
      >
        Schedule callback
      </button>
      <ScheduleCallbackModal
        open={open}
        interactionId={interactionId}
        initialPurpose={initialPurpose}
        onClose={() => setOpen(false)}
        onScheduled={handleScheduled}
      />
    </>
  );
}

const btnStyle: React.CSSProperties = {
  background: 'transparent',
  color: tokens.gold,
  border: `1px solid ${tokens.gold}`,
  borderRadius: 8,
  padding: '8px 14px',
  cursor: 'pointer',
  fontWeight: 600,
};
